import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
import { Platform, PlatformNoHandlers, useQuery } from "@core/api";
import { sanitizeOrganizationId } from "@core/configuration";
import Registry from "@core/registry";
import { createContext, useContext, useLayoutEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import { OrganizationLocalStorage } from "../OrganizationLocalStorage.js";
export var OrganizationContext = /*#__PURE__*/ createContext({
    organizationModelQuery: undefined,
    organizationDefinitionQuery: undefined
});
export var useOrganization = function() {
    var context = useContext(OrganizationContext);
    if (!context || !context.organizationModelQuery) {
        throw new Error("useOrganization must be used within an OrganizationProvider");
    }
    if (!context.organizationModelQuery.data) {
        throw new Error("Organization data is not available");
    }
    return context.organizationModelQuery.data;
};
export var useOrganizationDefinition = function() {
    var context = useContext(OrganizationContext);
    if (!context || !context.organizationDefinitionQuery) {
        throw new Error("useOrganizationDefinition must be used within an OrganizationProvider");
    }
    if (!context.organizationDefinitionQuery.data) {
        throw new Error("Organization definition data is not available");
    }
    return context.organizationDefinitionQuery.data;
};
/**
 * Fetch the organization model for the given organization ID.
 * @param currentOrg The ID of the organization to fetch.
 * @returns A promise that resolves to a tuple containing the organization model and a boolean indicating if the first org fallback was used.
 */ var fetchOrganizationModel = function() {
    var _ref = _async_to_generator(function(currentOrg) {
        var organization, e, ref, list, ref1;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _state.trys.push([
                        0,
                        2,
                        ,
                        4
                    ]);
                    if (!currentOrg || currentOrg === "unable" || currentOrg === "invites") {
                        throw new Error("Not a real organization, pick the first org instead");
                    }
                    return [
                        4,
                        PlatformNoHandlers.organization.get(currentOrg, {
                            additionalFields: "status"
                        })
                    ];
                case 1:
                    organization = _state.sent();
                    return [
                        2,
                        [
                            organization,
                            false
                        ]
                    ];
                case 2:
                    e = _state.sent();
                    return [
                        4,
                        Platform.organization.list({
                            page: 0,
                            perPage: 1,
                            sortBy: "displayName"
                        })
                    ];
                case 3:
                    list = _state.sent();
                    return [
                        2,
                        [
                            (ref1 = (ref = list.items) === null || ref === void 0 ? void 0 : ref[0]) !== null && ref1 !== void 0 ? ref1 : null,
                            true
                        ]
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    });
    return function fetchOrganizationModel(currentOrg) {
        return _ref.apply(this, arguments);
    };
}();
/**
 * Provides the organization context to its children.
 *
 * Constraint:
 * * has to be within context of the QueryClientProvider.
 */ export var OrganizationProvider = function(param) {
    var children = param.children, ignoreOrgChangeInUrl = param.ignoreOrgChangeInUrl;
    var ref, ref1, ref2;
    var navigate = useNavigate();
    var ref3;
    var organizationIdFromUrl = sanitizeOrganizationId((ref3 = (ref = useMatch("/:orgId/*")) === null || ref === void 0 ? void 0 : (ref1 = ref.params) === null || ref1 === void 0 ? void 0 : ref1.orgId) !== null && ref3 !== void 0 ? ref3 : "");
    var currentOrg = organizationIdFromUrl || OrganizationLocalStorage.getOrganizationFromLocalStorage() || null;
    var ref4 = _sliced_to_array(useState(false), 2), isUsingFirstOrg = ref4[0], setIsUsingFirstOrg = ref4[1];
    var organizationModelQuery = useQuery({
        queryKey: [
            "init",
            "organization",
            currentOrg
        ],
        queryFn: /*#__PURE__*/ _async_to_generator(function() {
            var ref, organization, hasUsedFirstOrgFallback, ref1, ref2;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            fetchOrganizationModel(currentOrg)
                        ];
                    case 1:
                        ref = _sliced_to_array.apply(void 0, [
                            _state.sent(),
                            2
                        ]), organization = ref[0], hasUsedFirstOrgFallback = ref[1];
                        OrganizationLocalStorage.setOrganizationInLocalStorage((ref1 = organization === null || organization === void 0 ? void 0 : organization.id) !== null && ref1 !== void 0 ? ref1 : "");
                        Registry.register("organization_id", (ref2 = organization === null || organization === void 0 ? void 0 : organization.id) !== null && ref2 !== void 0 ? ref2 : "");
                        Registry.register("organization", organization);
                        setIsUsingFirstOrg(hasUsedFirstOrgFallback);
                        if (hasUsedFirstOrgFallback && organization) {
                            navigate("/".concat(organization.id), {
                                replace: true
                            });
                        }
                        return [
                            2,
                            organization
                        ];
                }
            });
        }),
        retry: false,
        initialData: Registry.get("organization", {
            optional: true
        }),
        staleTime: Infinity
    });
    var organizationDefinitionQuery = useQuery({
        queryKey: [
            "init",
            "organization",
            "definition"
        ],
        queryFn: function() {
            return Platform.organization.getDefinition();
        },
        retry: false,
        initialData: Registry.get("organization_definition", {
            optional: true
        }),
        staleTime: Infinity,
        enabled: !!organizationModelQuery.data
    });
    useLayoutEffect(function() {
        var ref, ref1;
        if (!ignoreOrgChangeInUrl && (organizationModelQuery === null || organizationModelQuery === void 0 ? void 0 : (ref = organizationModelQuery.data) === null || ref === void 0 ? void 0 : ref.id) && organizationIdFromUrl && !isUsingFirstOrg && organizationIdFromUrl !== (organizationModelQuery === null || organizationModelQuery === void 0 ? void 0 : (ref1 = organizationModelQuery.data) === null || ref1 === void 0 ? void 0 : ref1.id)) {
            navigate(0); // reload page
        }
    }, [
        ignoreOrgChangeInUrl,
        organizationIdFromUrl,
        organizationModelQuery === null || organizationModelQuery === void 0 ? void 0 : (ref2 = organizationModelQuery.data) === null || ref2 === void 0 ? void 0 : ref2.id,
        isUsingFirstOrg
    ]);
    return /*#__PURE__*/ _jsx(OrganizationContext.Provider, {
        value: {
            organizationModelQuery: organizationModelQuery,
            organizationDefinitionQuery: organizationDefinitionQuery
        },
        children: children
    });
};

//# sourceMappingURL=OrganizationContext.js.map